import * as d3 from "d3"

export type HorizontalChartOption<T> = {
    x: (data: T) => number,
    edgeRadiuse?: number | "50%"//오른쪽의 둥근 반지름
    gap?: number //각 바의 간격
    barHeight?: (data: T) => number,
    marginRight: number,
    yAxisLabelDX?: number
    format?: string,
    yAxisLabel?: (data: T) => string,
    customIndexing?: (data: T) => number,
    maximumDataValue?: number
}

export default function HorizontalChart_1<T>(node: HTMLElement, datas: T[], option: HorizontalChartOption<T>) {
    let { width, height } = node.getBoundingClientRect();
    const {
        edgeRadiuse = "50%",
        x,
        barHeight = () => height / datas.length,
        gap = 10,
        marginRight,
        yAxisLabelDX = 10,
        format = "건",
        yAxisLabel = () => "",
        customIndexing,
        maximumDataValue
    } = option

    const X = d3.map(datas, x);
    const xDomain = [0, maximumDataValue ? maximumDataValue : d3.max(X) as number]; //X의 크기
    const mappedHeight = datas.map((v) => barHeight(v))


    const calcViewBoxHeight = (datas: T[], height: number) => {
        if (datas.length === 0) {
            return height;
        }

        const gapSum = gap * (datas.length - 1)
        const barHeightSum = datas.reduce((prev, cur) => {
            return prev + barHeight(cur)
        }, 0)
        const orizinSize = gapSum + barHeightSum
        return d3.max([orizinSize, height])
    }

    const calcYposition = (data: T) => {
        const index = datas.findIndex(v => v === data);
        const noneGapYposition = mappedHeight
            .slice(0, index)
            .reduce((prev, cur) => (prev + cur), 0)
        const previousGap = index === 0 ? 0 : index * gap;
        return noneGapYposition + previousGap
    }
    const xScale = (data: T) => {
        const xRange = [
            0,
            width - marginRight
        ]
        const value = x(data);
        const xScale = d3.scaleLinear(xDomain, xRange);

        return xScale(value)
    }

    const drawingPath = (data: T) => {
        const xPosition = 0;
        const startingY_Position = calcYposition(data);
        const height = barHeight(data);
        const width = xScale(data);
        const radiuse = edgeRadiuse === "50%"
            ? height / 2
            : edgeRadiuse > height / 2
                ? height / 2
                : edgeRadiuse;

        if (width - radiuse < 0) {
            return ``
        }
        const h = height - radiuse * 2 > 0
            ? `h${height - radiuse * 2}`
            : ``
        return `
            M${xPosition},${startingY_Position}
            h${width - radiuse}
            q${radiuse},0 ${radiuse},${radiuse}
            ${h}
            q0,${radiuse} -${radiuse},${radiuse}
            h-${width - radiuse}
            z
        `
    }

    const calcAxisLabelYPosition = (data: T) => {
        const startingY_Position = calcYposition(data);
        const height = barHeight(data);

        return startingY_Position + height / 2;
    }

    const pathStartY_Positions = datas.map(calcYposition);

    //Going crazy
    const svg = d3.create("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", `0, 0, ${width}, ${calcViewBoxHeight(datas, height)}`)
        .attr("style", "max-width: 100%;");
    node.append(svg.node() as SVGSVGElement)
    // 


    // Drawing Path
    const pathGroup = svg
        .append("g")
        .attr("class", "path-group");

    const paths = pathGroup
        .selectAll("path")
        .data(datas)
        .join("path")
        .attr("d", drawingPath)
        .attr("class", (d, i) => `bar bar-${customIndexing ? customIndexing(d) : i}`)

    //linear x axis
    const xAxisGroup = svg
        .append("g")
        .attr("class", "linear-xAxis-g")

    const xAxisLabels = xAxisGroup
        .selectAll("foreignObject")
        .data(datas)
        .join("foreignObject")
        .attr("y", calcYposition)
        .attr("x", width - marginRight + yAxisLabelDX)
        .attr("width", marginRight - yAxisLabelDX)
        .attr("height", barHeight)
        .attr("index", (d, i) => customIndexing ? customIndexing(d) : i)

    xAxisLabels.append("xhtml:div")
        .text((d) => `${x(d)}${format}`)
        .attr("class", (d, i) => `xAxis-label xAxis-label-${customIndexing ? customIndexing(d) : i}`)

    // Linear y Axis 
    const yAxisGroup = svg
        .append("g")
        .attr("class", "linear-yAxis-g")

    const yAxisLabels = yAxisGroup
        .selectAll("text")
        .data(datas)
        .join("text")
        .attr("y", calcAxisLabelYPosition)
        .attr("x", yAxisLabelDX)
        .attr("index", (_, i) => i)
        .attr("fill", "white")
        .attr("alignment-baseline", "central")
        .attr("dy", "-0.1em")
        .attr("class", (d, i) => `yAxis-label yAxis-label-${customIndexing ? customIndexing(d) : i}`)


    const yAxisLabelOverflowElipsis = () => {
        yAxisLabels
            .text(yAxisLabel)

        yAxisLabels
            .nodes()
            .filter((text, i) => {
                const node = text as SVGTextElement
                const rectWidth = xScale(datas[i]);
                const textWidth = node.getComputedTextLength() + 10;
                return (rectWidth - yAxisLabelDX) >= textWidth
            })
            .forEach((text) => {
                const node = text as SVGTextElement
                node.classList.remove("right")
            })
        yAxisLabels
            .nodes()
            .filter((text, i) => {
                const node = text as SVGTextElement
                const rectWidth = xScale(datas[i]);
                const textWidth = node.getComputedTextLength() + 10;
                return (rectWidth - yAxisLabelDX) < textWidth
            })
            .forEach((text) => {
                const node = text as SVGTextElement
                const i = Number(node.getAttribute("index"))
                const rectWidth = xScale(datas[i])
                const textWidth = node.getComputedTextLength() + (yAxisLabelDX || 0);
                const emptySpaceWidth = width - rectWidth - marginRight;

                if (emptySpaceWidth >= textWidth) {
                    node.setAttribute("x", String(rectWidth))
                    node.setAttribute("dx", String(yAxisLabelDX))
                    node.classList.add("right-complete-short")
                    node.classList.add("right")
                } else {
                    // Elipsis
                    if (emptySpaceWidth >= rectWidth) {
                        node.setAttribute("x", String(rectWidth))
                        node.setAttribute("dx", String(yAxisLabelDX))
                        node.classList.add("right-complete-short")
                        node.classList.add("right")
                    }

                    const baseWidth = emptySpaceWidth >= rectWidth ? emptySpaceWidth : rectWidth;
                    const text = yAxisLabel(datas[i])
                    const words = text.split("")

                    while (node.getComputedTextLength() > baseWidth && words.length) {
                        words.pop();
                        node.textContent = words.join('') + "..."
                    }

                }


            })

    }
    yAxisLabelOverflowElipsis()
    let timeout: any

    const resizeEvent = () => {
        width = node.getBoundingClientRect().width;
        height = node.getBoundingClientRect().height;
        if (timeout) {
            clearTimeout(timeout)
        }

        timeout = setTimeout(() => {
            // SVG
            svg
                .attr("width", width)
                .attr("height", height)
                .attr("viewBox", `0, 0, ${width}, ${calcViewBoxHeight(datas, height)}`)
            // Path
            paths
                .attr("d", drawingPath)

            // xAxis
            xAxisLabels
                .attr("y", calcYposition)
                .attr("x", width - marginRight + yAxisLabelDX)
                .attr("width", marginRight - yAxisLabelDX)
                .attr("height", barHeight)
            // yAxis
            yAxisLabels
                .attr("y", calcAxisLabelYPosition)
                .attr("x", yAxisLabelDX);

            yAxisLabelOverflowElipsis()
        }, 100)

    }

    window.addEventListener("resize", resizeEvent);

    return {
        resizeEvent,
        node: svg.node() as SVGSVGElement
    }

}